import { useState, useEffect, useRef, useCallback } from 'react';

interface QuantityControlProps {
  quantity: number;
  onChange: (quantity: number) => void;
  disabled?: boolean;
  debounceMs?: number;
}

export function QuantityControl({
  quantity,
  onChange,
  disabled = false,
  debounceMs = 500,
}: QuantityControlProps) {
  const [localValue, setLocalValue] = useState(quantity.toString());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialHoldRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localValueRef = useRef(localValue);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    setLocalValue(quantity.toString());
  }, [quantity]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
      if (initialHoldRef.current) clearTimeout(initialHoldRef.current);
    };
  }, []);

  const debouncedOnChange = useCallback((value: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange(value);
    }, debounceMs);
  }, [onChange, debounceMs]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      debouncedOnChange(numValue);
    }
  };

  const handleInputBlur = () => {
    const numValue = parseInt(localValue, 10);
    if (isNaN(numValue) || numValue < 1) {
      setLocalValue(quantity.toString());
    } else if (numValue !== quantity) {
      onChange(numValue);
    }
  };

  const incrementValue = useCallback((delta: number) => {
    setLocalValue(prev => {
      const current = parseInt(prev, 10) || quantity;
      const newValue = Math.max(1, current + delta);
      return newValue.toString();
    });
  }, [quantity]);

  const startHold = useCallback((delta: number) => {
    incrementValue(delta);

    initialHoldRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        incrementValue(delta);
      }, 100);
    }, 500);
  }, [incrementValue]);

  const stopHold = useCallback(() => {
    if (initialHoldRef.current) {
      clearTimeout(initialHoldRef.current);
      initialHoldRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }

    const numValue = parseInt(localValueRef.current, 10);
    if (!isNaN(numValue) && numValue >= 1 && numValue !== quantity) {
      onChange(numValue);
    }
  }, [quantity, onChange]);

  const handlePointerDown = useCallback((delta: number) => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    startHold(delta);
  }, [startHold]);

  const handlePointerUp = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    stopHold();
  }, [stopHold]);

  return (
    <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white touch-manipulation">
      <button
        type="button"
        className="w-11 h-11 border-none bg-gray-100 text-xl font-semibold text-gray-800 cursor-pointer flex items-center justify-center select-none touch-manipulation border-r border-gray-300 hover:enabled:bg-gray-200 active:enabled:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        onMouseDown={handlePointerDown(-1)}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown(-1)}
        onTouchEnd={handlePointerUp}
        onTouchCancel={handlePointerUp}
        disabled={disabled || quantity <= 1}
        title="Quitar una unidad"
      >
        -
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="w-12 h-11 border-none text-center text-base font-medium text-gray-800 bg-transparent focus:outline-none disabled:opacity-60 touch-manipulation [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        disabled={disabled}
        title="Cantidad de unidades"
      />
      <button
        type="button"
        className="w-11 h-11 border-none bg-gray-100 text-xl font-semibold text-gray-800 cursor-pointer flex items-center justify-center select-none touch-manipulation border-l border-gray-300 hover:enabled:bg-gray-200 active:enabled:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        onMouseDown={handlePointerDown(1)}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown(1)}
        onTouchEnd={handlePointerUp}
        onTouchCancel={handlePointerUp}
        disabled={disabled}
        title="Agregar una unidad"
      >
        +
      </button>
    </div>
  );
}
